import { defineStore } from 'pinia'
import api from '../lib/axios'
import { orgInitials } from '../utils/personIdentity.js'
import { saveModuleState } from '../composables/useModuleState'

const COLORS = ['#f97316', '#8b5cf6', '#3b82f6', '#f59e0b', '#15803d', '#0e7490', '#b45309', '#a21caf']

function wsColor(id) {
    return COLORS[id % COLORS.length]
}

// One rule for every avatar that stands for a thing rather than a person, so a workspace
// and the project inside it cannot spell their names two different ways.

function decorate(ws) {
    return { ...ws, color: wsColor(ws.id), initials: orgInitials(ws.name) }
}

const PROJ_COLORS = ['#f97316', '#8b5cf6', '#3b82f6', '#f59e0b', '#15803d', '#0e7490', '#b45309', '#a21caf']

function projColor(id) {
    return PROJ_COLORS[id % PROJ_COLORS.length]
}



function decorateProject(p) {
    return { ...p, color: projColor(p.id), initials: orgInitials(p.name) }
}

export const useWorkspaceStore = defineStore('workspace', {
    state: () => ({
        workspaces: [],
        activeWorkspaceId: null,
        projects: [],
        activeProjectId: null,
        lastPage: 'dashboard',
        // workspaceId -> the project last open in it. Switching workspace used to jump to
        // the first project alphabetically, throwing away a choice the app was already
        // saving and already restoring on reload.
        rememberedProjects: {},
    }),

    getters: {
        activeWorkspace: (state) =>
            state.workspaces.find(w => w.id === state.activeWorkspaceId) ?? state.workspaces[0] ?? null,

        companyWorkspaces: (state) => state.workspaces,

        activeProject: (state) =>
            state.projects.find(p => p.id === state.activeProjectId) ?? null,

        workspaceProjects: (state) => state.projects,
    },

    actions: {
        hydrateFromModuleStates(states) {
            const global = states.find(s => s.module === 'global' && s.workspace_id === 0 && s.submodule === '')
            if (global?.state?.active_workspace_id) {
                this.activeWorkspaceId = global.state.active_workspace_id
            }

            if (this.activeWorkspaceId) {
                const wsState = states.find(s =>
                    s.module === 'workspace' &&
                    s.workspace_id === this.activeWorkspaceId &&
                    s.submodule === ''
                )
                if (wsState?.state?.active_project_id) {
                    this.activeProjectId = wsState.state.active_project_id
                }
                if (wsState?.state?.last_page) {
                    this.lastPage = wsState.state.last_page
                }
            }

            // Every workspace's remembered project, so switching to one restores it the
            // same way reloading into it already does.
            for (const st of states) {
                if (st.module === 'workspace' && st.submodule === '' && st.state?.active_project_id) {
                    this.rememberedProjects[st.workspace_id] = st.state.active_project_id
                }
            }
        },

        async loadWorkspaces(companyId) {
            const params = companyId ? { company_id: companyId } : {}
            const { data } = await api.get('/workspaces', { params })
            this.workspaces = (data.data ?? []).map(decorate)

            const ids = this.workspaces.map(w => w.id)
            if (!this.activeWorkspaceId || !ids.includes(this.activeWorkspaceId)) {
                this.activeWorkspaceId = this.workspaces[0]?.id ?? null
                if (this.activeWorkspaceId) {
                    saveModuleState(0, 'global', '', { active_workspace_id: this.activeWorkspaceId })
                }
            }
        },

        async loadProjects(workspaceId) {
            if (!workspaceId) {
                this.projects = []
                return
            }
            const { data } = await api.get('/projects', { params: { workspace_id: workspaceId } })
            this.projects = (data.data ?? []).map(decorateProject)
        },

        /**
         * The project to open on arriving at a workspace: the one last open there, or the
         * first alphabetically on a genuinely first visit.
         *
         * The remembered id is validated against the loaded list, because a project can be
         * deleted or access revoked between visits and a stale id would 404.
         */
        projectToOpen(workspaceId) {
            const remembered = this.rememberedProjects[workspaceId]
            const match = remembered ? this.projects.find(p => p.id === remembered) : null

            return match ?? this.projects[0] ?? null
        },

        setWorkspace(id) {
            this.activeWorkspaceId = id
            this.activeProjectId = null
            this.projects = []
            saveModuleState(0, 'global', '', { active_workspace_id: id })
        },

        /**
         * Spec 006 — put the store into the context a readable URL names.
         *
         * /projects/THC/tickets cannot be resolved locally on a cold load: this store only
         * ever holds one workspace's projects, and it does not yet know which workspace THC
         * belongs to. So an unknown code costs one round trip; a code already loaded costs
         * nothing.
         *
         * Returns the project, or null when the code does not resolve — the caller decides
         * whether that is a 404 or a redirect.
         */
        async resolveProjectByCode(code, workspaceSlug = null) {
            if (!code) return null

            const wanted = String(code).toUpperCase()
            // The loaded projects are one workspace's, which may not be the link's: with a slug,
            // only a project in that workspace counts, or /other-ws/RACER opened this RACER.
            const slugOf = id => this.workspaces.find(w => w.id === id)?.slug
            const known = this.projects.find(p => (p.short_code ?? '').toUpperCase() === wanted
                && (!workspaceSlug || slugOf(p.workspace_id) === workspaceSlug))

            if (known) {
                if (this.activeProjectId !== known.id) this.setProject(known.id)

                return known
            }

            let project
            try {
                // The workspace picks the project when the code exists in more than one company.
                const { data } = await api.get(`/projects/by-code/${encodeURIComponent(code)}`, { params: workspaceSlug ? { workspace: workspaceSlug } : {} })
                project = data.data
            } catch {
                // 404 (no such code) or 409 (the code names more than one project). Neither
                // is something the store can resolve.
                return null
            }

            if (!project) return null

            if (this.activeWorkspaceId !== project.workspace_id) {
                this.activeWorkspaceId = project.workspace_id
                saveModuleState(0, 'global', '', { active_workspace_id: project.workspace_id })
            }

            // The switcher and every project-scoped page read this list, so it has to hold
            // the workspace we just moved to rather than the one we came from.
            //
            // Caught, because this runs inside router.beforeEach and the project is already
            // resolved by here. An exception escaping a guard aborts the navigation with no
            // URL change and nothing on screen — the link simply stops working. A stale
            // project list is a far smaller problem, and the page reloads its own anyway.
            await this.loadProjects(project.workspace_id).catch(() => {})
            this.setProject(project.id)

            return this.projects.find(p => p.id === project.id) ?? decorateProject(project)
        },

        /**
         * Spec 006 — /workspaces/the-hills-rehab/incoming. The workspace list is loaded for
         * the whole company, so this usually resolves locally; it only fetches when the
         * list has not been hydrated yet.
         */
        async resolveWorkspaceBySlug(slug) {
            if (!slug) return null

            if (this.workspaces.length === 0) {
                // Caught for the same reason as above: this runs inside the router guard.
                // An empty list here is not fatal — the by-slug lookup below still answers,
                // and a null answer is a redirect the user can see rather than a dead click.
                await this.loadWorkspaces().catch(() => {})
            }

            let workspace = this.workspaces.find(w => w.slug === slug)

            // Not in the list means it belongs to another company — this store only ever
            // holds the active company's workspaces. A shared link is exactly that case, so
            // ask the server rather than treating it as unknown.
            if (!workspace) {
                try {
                    const { data } = await api.get(`/workspaces/by-slug/${encodeURIComponent(slug)}`)
                    workspace = data.data
                } catch {
                    return null
                }
            }

            if (!workspace) return null

            if (this.activeWorkspaceId !== workspace.id) {
                this.activeWorkspaceId = workspace.id
                saveModuleState(0, 'global', '', { active_workspace_id: workspace.id })
                // THE one that broke "Open full ticket". A cross-project list shows you a
                // ticket in a workspace whose project list you may not be able to read, so
                // this 403s, the throw escapes the guard, and the button does nothing at
                // all. The workspace is already resolved; priming its projects is not worth
                // a cancelled navigation.
                await this.loadProjects(workspace.id).catch(() => {})
            }

            return workspace
        },

        setProject(id) {
            this.activeProjectId = id
            if (this.activeWorkspaceId && id) {
                this.rememberedProjects[this.activeWorkspaceId] = id
            }
            if (this.activeWorkspaceId) {
                saveModuleState(this.activeWorkspaceId, 'workspace', '', {
                    active_project_id: id,
                    last_page: this.lastPage,
                })
            }
        },

        clearProject() {
            this.setProject(null)
        },

        syncFromUrl(wsId, projId, page) {
            const wsChanged = wsId && wsId !== this.activeWorkspaceId
            const projChanged = projId && projId !== this.activeProjectId

            if (wsChanged) {
                this.activeWorkspaceId = wsId
                saveModuleState(0, 'global', '', { active_workspace_id: wsId })
            }
            if (projChanged) {
                this.activeProjectId = projId
            }
            if (page && page !== this.lastPage) {
                this.lastPage = page
            }
            // Persist workspace-scoped state whenever page tracking changes
            if (wsId && projId && (projChanged || page)) {
                saveModuleState(wsId, 'workspace', '', {
                    active_project_id: projId,
                    last_page: page || this.lastPage,
                })
            }
        },
    },
})
